import { useEffect, useMemo, useState, type ReactNode } from 'react';
import L from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { markerSvg } from '../../../../shared/contracts/map-activity.js';
import { GeoMap, resolveTiles } from './GeoMap.js';
import { buildActivityQuery } from './geoApi.js';
import type { ActivityFilterState, ActivityLegend, ActivityPoint, MapConfig } from './geoApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface PhotoMeta {
  photoId: string;
  category: string;
  capturedAt: string | null;
  accuracyM: number | null;
  uploadStatus: string;
}
interface PointDetail {
  point: {
    adId: string;
    vendorName: string | null;
    mediumName: string | null;
    territoryName: string | null;
    districtName: string | null;
    customerName: string | null;
    jobStatus: string | null;
    pointCapturedAt: string | null;
    accuracyM: number | null;
    latitude: number | null;
    longitude: number | null;
  };
  beforePhotos: PhotoMeta[];
  afterPhotos: PhotoMeta[];
  gpsReadings: unknown[];
}
interface Kpis {
  totalAdvertisements: number;
  totalVendors: number;
  totalActiveTerritories: number;
  territoriesCovered: number;
  coveragePct: number;
  dryTerritories: number;
}

/** A job that the officers verified is an advertisement with its evidence on file. */
const EVIDENCED_STAGES = new Set(['OFFICER_VERIFIED', 'PAID']);

function selectedValues(el: HTMLSelectElement): string[] {
  return Array.from(el.selectedOptions).map((o) => o.value);
}

function toggled(list: string[], value: string, on: boolean): string[] {
  return on ? [...new Set([...list, value])] : list.filter((v) => v !== value);
}

/**
 * §9-§11 Geo Intelligence Dashboard — the activity map. Every activity that has
 * a position is drawn at the stage it has reached: in its sub-type's shape and
 * its stage's colour, both set by the tenant (Branding activity types, Geo
 * Configuration). The reader ticks which stages and which activities to see.
 */
export function GeoIntelligenceDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const [mapConfig, setMapConfig] = useState<MapConfig | null>(null);
  const [legend, setLegend] = useState<ActivityLegend | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [filter, setFilter] = useState<ActivityFilterState | null>(null);
  const [points, setPoints] = useState<ActivityPoint[]>([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [detail, setDetail] = useState<PointDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Kept apart: loading the points clears `error`, and must not clear this.
  const [kpiError, setKpiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<{ data: MapConfig }>('/v1/geo-intel/map-config', { token }),
      apiRequest<{ data: ActivityLegend }>('/v1/geo-intel/activity-legend', { token }),
    ])
      .then(([mc, lg]) => {
        if (!alive) return;
        setMapConfig(mc.data);
        setLegend(lg.data);
        // Everything that has a point is ticked to begin with; the reader
        // narrows from there.
        setFilter({
          stages: lg.data.stages.map((s) => s.stageCode),
          itemIds: lg.data.items.map((i) => i.brandingItemId),
          vendorIds: [],
          territoryIds: [],
          dateFrom: '',
          dateTo: '',
        });
      })
      .catch((e) => {
        if (alive) setError(e instanceof ApiError && e.status === 403
          ? t('m16report.geo.forbidden')
          : (e as Error).message);
      });
    // KPIs need EXECUTIVE_VIEW: a 403 leaves the tiles out, anything else is said.
    apiRequest<{ data: { kpis: Kpis } }>('/v1/geo-intel/executive/dashboard', { token })
      .then((r) => { if (alive) setKpis(r.data.kpis); })
      .catch((e) => {
        if (alive && !(e instanceof ApiError && e.status === 403)) setKpiError((e as Error).message);
      });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!filter) return;
    let alive = true;
    setLoading(true);
    setError(null);
    apiRequest<{ data: { points: ActivityPoint[]; total: number; capped: boolean } }>(
      `/v1/geo-intel/activities${buildActivityQuery(filter)}`, { token },
    )
      .then((r) => {
        if (!alive) return;
        setPoints(r.data.points);
        setTotal(r.data.total);
        setCapped(r.data.capped);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.geo.forbidden')
          : (e as Error).message);
        setPoints([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // The checkboxes apply at once; the filter panel applies on its button.
  }, [token, filter?.stages, filter?.itemIds, reload]);

  const colourOf = useMemo(() => {
    const m = new Map((legend?.stages ?? []).map((s) => [s.stageCode, s.colourHex]));
    return (stage: string) => m.get(stage) ?? '#9E9E9E';
  }, [legend]);

  const iconFor = useMemo(() => {
    const cache = new Map<string, L.DivIcon>();
    return (shape: string | null, stage: string): L.DivIcon => {
      const key = `${shape}|${stage}`;
      let icon = cache.get(key);
      if (!icon) {
        icon = L.divIcon({
          html: markerSvg(shape, colourOf(stage)),
          className: '',
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        cache.set(key, icon);
      }
      return icon;
    };
  }, [colourOf]);

  const openDetail = (activityId: string): void => {
    setDetail(null);
    apiRequest<{ data: PointDetail }>(`/v1/geo-intel/points/${activityId}`, { token })
      .then((r) => setDetail(r.data))
      .catch((e) => setError((e as Error).message));
  };

  const center: [number, number] = mapConfig
    ? [mapConfig.defaultCenter.lat, mapConfig.defaultCenter.lng]
    : [22.9734, 78.6569];
  const zoom = mapConfig?.defaultZoom ?? 5;
  const tiles = resolveTiles(mapConfig);

  return (
    <main aria-label="geo-intel-dashboard">
      <h1>{t('m16report.geo.title')}</h1>

      {error && <p role="alert" aria-label="geo-error">{error}</p>}
      {kpiError && <p role="alert" aria-label="kpi-error">{kpiError}</p>}

      {kpis && (
        <section aria-label="geo-kpis" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span aria-label="kpi-total-ads">{t('m16report.geo.kpi.ads', { value: kpis.totalAdvertisements })}</span>
          <span aria-label="kpi-vendors">{t('m16report.geo.kpi.vendors', { value: kpis.totalVendors })}</span>
          <span aria-label="kpi-coverage">{t('m16report.geo.kpi.coverage', { value: kpis.coveragePct })}</span>
          <span aria-label="kpi-dry">{t('m16report.geo.kpi.dry', { value: kpis.dryTerritories })}</span>
        </section>
      )}

      {legend && filter && (
        <section aria-label="activity-pickers" style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <fieldset aria-label="stage-pickers">
            <legend>{t('m16report.activity.stages')}</legend>
            {legend.stages.map((s) => (
              <label key={s.stageCode} style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  aria-label={`stage-${s.stageCode}`}
                  checked={filter.stages.includes(s.stageCode)}
                  onChange={(e) => setFilter({ ...filter, stages: toggled(filter.stages, s.stageCode, e.target.checked) })}
                />{' '}
                <span
                  aria-hidden="true"
                  style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: s.colourHex, verticalAlign: 'middle' }}
                />{' '}
                {t('m16report.activity.count', { name: t(`m16report.stage.${s.stageCode}`), count: s.count })}
              </label>
            ))}
          </fieldset>
          <fieldset aria-label="activity-item-pickers">
            <legend>{t('m16report.activity.activities')}</legend>
            {legend.items.length === 0 && <p>{t('m16report.activity.noneYet')}</p>}
            {legend.items.map((i) => (
              <label key={i.brandingItemId} style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  aria-label={`item-${i.brandingItemId}`}
                  checked={filter.itemIds.includes(i.brandingItemId)}
                  onChange={(e) => setFilter({ ...filter, itemIds: toggled(filter.itemIds, i.brandingItemId, e.target.checked) })}
                />{' '}
                <img alt="" width={16} height={16} style={{ verticalAlign: 'middle' }}
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(markerSvg(i.mapShape, '#FFFFFF', 16))}`} />{' '}
                {t('m16report.activity.count', { name: i.itemName, count: i.count })}
              </label>
            ))}
          </fieldset>
        </section>
      )}

      {legend && filter && (
        <section aria-label="geo-filter-panel">
          <label>{t('m16report.geo.filter.vendors')}
            <select aria-label="filter-vendors" multiple value={filter.vendorIds}
              onChange={(e) => setFilter({ ...filter, vendorIds: selectedValues(e.target) })}>
              {legend.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label>{t('m16report.geo.filter.territories')}
            <select aria-label="filter-territories" multiple value={filter.territoryIds}
              onChange={(e) => setFilter({ ...filter, territoryIds: selectedValues(e.target) })}>
              {legend.territories.map((tr) => <option key={tr.id} value={tr.id}>{tr.name}</option>)}
            </select>
          </label>
          <label>{t('m16report.geo.filter.from')}
            <DateInput aria-label="filter-date-from" type="date" value={filter.dateFrom}
              onValueChange={(nextValue) => setFilter({ ...filter, dateFrom: nextValue })} />
          </label>
          <label>{t('m16report.geo.filter.to')}
            <DateInput aria-label="filter-date-to" type="date" value={filter.dateTo}
              onValueChange={(nextValue) => setFilter({ ...filter, dateTo: nextValue })} />
          </label>
          <Button variant="secondary" type="button" aria-label="apply-filters" onClick={() => setReload((n) => n + 1)}>{t('m16report.geo.filter.apply')}</Button>
          <Button variant="secondary" type="button" aria-label="reset-filters"
            onClick={() => { setFilter({ ...filter, vendorIds: [], territoryIds: [], dateFrom: '', dateTo: '' }); setReload((n) => n + 1); }}>
            {t('m16report.geo.filter.reset')}
          </Button>
        </section>
      )}

      {mapConfig && (
        <p aria-label="map-provider">
          {t('m16report.geo.mapProvider', { provider: mapConfig.provider, fallback: mapConfig.usingFreeFallback ? t('m16report.geo.mapProvider.fallback') : '' })}
        </p>
      )}

      {loading && <p aria-label="geo-loading">{t('m16report.geo.loading')}</p>}

      {filter && (filter.stages.length === 0 || filter.itemIds.length === 0) && (
        <p aria-label="nothing-ticked">{t('m16report.activity.tick')}</p>
      )}

      {capped && (
        <p aria-label="cluster-warning">{t('m16report.geo.cluster', { shown: points.length, total })}</p>
      )}

      <GeoMap center={center} zoom={zoom} tiles={tiles} ariaLabel="geo-map">
        {points.map((p) => (
          <Marker
            key={p.activityId}
            position={[p.latitude, p.longitude]}
            icon={iconFor(p.mapShape, p.stageCode)}
          >
            <Popup>
              <div aria-label={`point-popup-${p.activityId}`}>
                <strong>{p.itemName ?? t('m16report.geo.point.advertisement')}</strong> — {p.reference ?? '—'}<br />
                {t('m16report.activity.point.stage', { value: t(`m16report.stage.${p.stageCode}`) })}<br />
                {t('m16report.activity.point.customer', { value: p.customerName ?? '—' })}<br />
                {t('m16report.geo.point.vendor', { value: p.vendorName ?? '—' })}<br />
                {t('m16report.geo.point.territory', { value: p.territoryName ?? '—' })}<br />
                {t('m16report.geo.point.date', { value: p.activityDate ? atDay(p.activityDate) : '—' })}<br />
                {t('m16report.activity.point.position', { value: t(`m16report.activity.source.${p.locationSource}`) })}<br />
                {p.activitySource === 'JOB_REQUEST' && EVIDENCED_STAGES.has(p.stageCode) && (
                  <Button variant="secondary" type="button" aria-label={`detail-${p.activityId}`} onClick={() => openDetail(p.activityId)}>{t('m16report.geo.point.details')}</Button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </GeoMap>

      {detail && (
        <section aria-label="point-detail">
          <h2>{t('m16report.geo.detail.title')}</h2>
          <ul>
            <li aria-label="detail-vendor">{t('m16report.geo.detail.vendor', { value: detail.point.vendorName ?? '—' })}</li>
            <li aria-label="detail-medium">{t('m16report.geo.detail.medium', { value: detail.point.mediumName ?? '—' })}</li>
            <li aria-label="detail-territory">{t('m16report.geo.detail.territory', { territory: detail.point.territoryName ?? '—', district: detail.point.districtName ?? '—' })}</li>
            <li aria-label="detail-customer">{t('m16report.geo.detail.customer', { value: detail.point.customerName ?? '—' })}</li>
            <li aria-label="detail-status">{t('m16report.geo.detail.status', { value: detail.point.jobStatus ?? '—' })}</li>
            <li aria-label="detail-accuracy">{t('m16report.geo.detail.accuracy', { value: detail.point.accuracyM ?? '—' })}</li>
            <li aria-label="detail-photos">{t('m16report.geo.detail.photos', { before: detail.beforePhotos.length, after: detail.afterPhotos.length })}</li>
          </ul>
          <p aria-label="detail-photo-note">{t('m16report.geo.detail.photoNote')}</p>
          <Button variant="secondary" type="button" aria-label="close-detail" onClick={() => setDetail(null)}>{t('m16report.geo.detail.close')}</Button>
        </section>
      )}
    </main>
  );
}
